import React, { useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { getApi } from "../../../helpers/auth_helpers";
import { SEARCH_SCOPES, SearchScope } from "./productListFilters";
import { SearchMatch, matchQueries, matchUrl, mergeMatches } from "./searchMatches";
import { formatRupee } from "./productListRow";
import { ProductThumb } from "./ProductListBits";
import { PlIcon } from "./plIcons";

const RECENT_KEY = "hkp.productList.recentSearches";

const readRecent = (): string[] => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string").slice(0, 5) : [];
  } catch {
    return [];
  }
};

const rememberSearch = (text: string) => {
  const value = text.trim();
  if (value.length < 2) return;
  try {
    const next = [value, ...readRecent().filter((v) => v.toLowerCase() !== value.toLowerCase())].slice(0, 5);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* storage full or blocked: recent searches are only a convenience */
  }
};

/** Live matches for the typed text (separate small requests; the list waits for Enter / typing pause). */
const useMatches = (active: boolean, scope: SearchScope, text: string) => {
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    const queries = active ? matchQueries(scope, text) : [];
    const mine = ++seq.current;
    if (!queries.length) {
      setMatches([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      const base = process.env.REACT_APP_SERVERURL || "";
      Promise.all(
        queries.map((q) =>
          Promise.resolve(getApi(matchUrl(base, q, text)))
            .then((res: any) => ({ by: q.by, docs: Array.isArray(res?.data?.docs) ? res.data.docs : [] }))
            .catch(() => ({ by: q.by, docs: [] }))
        )
      ).then((groups) => {
        if (mine !== seq.current) return;
        setMatches(mergeMatches(groups));
        setLoading(false);
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [active, scope, text]);
  return { matches, loading };
};

type Props = {
  value: string;
  scope: SearchScope;
  onChange: (text: string) => void;
  onScopeChange: (scope: SearchScope) => void;
  /** Run the search now (Enter), skipping the typing delay. */
  onSubmit: () => void;
  /** A match was picked: open that product. */
  onOpenProduct: (product: any) => void;
  placeholder: string;
  /** Phone: tapping the box opens the full-screen search (P02). */
  fullScreen?: boolean;
  /** Laptop: show the "/" key hint. */
  keyHint?: boolean;
};

const snOf = (product: any) =>
  product?.orgSrNo != null && product.orgSrNo !== "" ? `#${product.orgSrNo}` : product?.productUniqueId ? `#${product.productUniqueId}` : "";

/**
 * One box for name, HKP tag, product ID, tag and colour. "Everything" sends the plain-language
 * search (q, AI guesses on); a "Look in" chip narrows it to one field. Matches show as you type.
 */
const ProductSearchBox = ({ value, scope, onChange, onScopeChange, onSubmit, onOpenProduct, placeholder, fullScreen, keyHint }: Props) => {
  const [focused, setFocused] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const screenInputRef = useRef<HTMLInputElement | null>(null);
  const openedWith = useRef("");
  const scopeLabel = SEARCH_SCOPES.find((s) => s.key === scope)?.label || "Everything";
  const { matches, loading } = useMatches(focused, scope, value);

  const open = () => {
    if (!focused) openedWith.current = value;
    setRecent(readRecent());
    if (fullScreen && !focused) {
      // Render the search screen now and focus its input inside this tap: iPhone only raises the
      // keyboard for focus that happens in the tap itself, not a frame later.
      flushSync(() => setFocused(true));
      screenInputRef.current?.focus();
      return;
    }
    setFocused(true);
  };
  const close = () => {
    setFocused(false);
    rememberSearch(value);
  };

  // Full-screen search: lock the page behind it and focus its own input.
  useEffect(() => {
    if (!fullScreen || !focused) return undefined;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => screenInputRef.current?.focus());
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [fullScreen, focused]);

  const submit = () => {
    rememberSearch(value);
    onSubmit();
    if (fullScreen) setFocused(false);
    else inputRef.current?.blur();
  };

  const pickMatch = (product: any) => {
    rememberSearch(value);
    setFocused(false);
    onOpenProduct(product);
  };

  const input = (ref: React.RefObject<HTMLInputElement>, onScreen: boolean) => (
    <input
      ref={ref}
      type="search"
      enterKeyHint="search"
      aria-label={`Search products${scope !== "all" ? ` by ${scopeLabel}` : ""}`}
      placeholder={scope === "all" ? placeholder : `Search ${scopeLabel.toLowerCase()}…`}
      value={value}
      readOnly={fullScreen && !onScreen}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          submit();
        } else if (event.key === "Escape") {
          if (onScreen) setFocused(false);
          else inputRef.current?.blur();
        }
      }}
    />
  );

  const clearButton = (ref: React.RefObject<HTMLInputElement>) =>
    value ? (
      <button
        type="button"
        className="pl-search__clear"
        aria-label="Clear search"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          onChange("");
          ref.current?.focus();
        }}
      >
        <PlIcon name="x" size={16} />
      </button>
    ) : null;

  const panelBody = (
    <>
      <div className="pl-search-sec">
        <div className="pl-sec">Look in</div>
        <div className="pl-chips is-wrap" role="radiogroup" aria-label="Look in">
          {SEARCH_SCOPES.map((s) => (
            <button
              key={s.key}
              type="button"
              role="radio"
              aria-checked={scope === s.key}
              className={`pl-chip${scope === s.key ? " is-on" : ""}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onScopeChange(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <p className="pl-note">One box searches all of these. Tap one to search only there.</p>
      </div>
      {value.trim().length >= 2 ? (
        <div className="pl-search-sec is-ruled">
          <div className="pl-sec">Matches</div>
          {matches.length ? (
            <div className="pl-matches">
              {matches.map(({ product, byId }) => {
                const price = formatRupee(product?.sellPrice);
                return (
                  <button
                    key={product?._id || product?.productUniqueId}
                    type="button"
                    className="pl-match"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => pickMatch(product)}
                  >
                    <ProductThumb product={product} className="is-row" onPreview={() => pickMatch(product)} />
                    <span className="pl-grow">
                      <span className="pl-match__name">{product?.name || "Untitled product"}</span>
                      <span className="pl-meta">{[snOf(product), price].filter(Boolean).join(" · ")}</span>
                    </span>
                    {byId ? <span className="pl-badge is-blue">ID</span> : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="pl-note">{loading ? "Looking…" : "No quick matches. Press Enter to search the whole list."}</p>
          )}
        </div>
      ) : null}
      {recent.length ? (
        <div className="pl-search-sec is-ruled">
          <div className="pl-sec">Recent</div>
          <div className="pl-recent">
            {recent.map((text) => (
              <button
                key={text}
                type="button"
                className="pl-recent__item"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(text);
                  rememberSearch(text);
                  onSubmit();
                  if (fullScreen) setFocused(false);
                }}
              >
                <PlIcon name="clock" size={20} />
                <span className="pl-grow">{text}</span>
                <PlIcon name="right" size={16} className="pl-recent__chev" />
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {scope === "all" ? (
        <p className="pl-alert is-info">
          <PlIcon name="spark" size={18} />
          <span>
            <b>Describe it:</b> “red saree after 2025 in stock” also works. HKP reads it and narrows the list.
          </span>
        </p>
      ) : null}
    </>
  );

  return (
    <div
      className={`pl-search-wrap${focused && !fullScreen ? " is-focused" : ""}`}
      ref={wrapRef}
      onFocus={(event) => {
        if (!fullScreen || event.target === inputRef.current) open();
      }}
      onBlur={(event) => {
        if (!fullScreen && !wrapRef.current?.contains(event.relatedTarget as Node)) close();
      }}
    >
      <label className="pl-search" onClick={fullScreen ? open : undefined}>
        <PlIcon name="search" />
        {input(inputRef, false)}
        {scope !== "all" ? <span className="pl-search__scope">{scopeLabel}</span> : null}
        {fullScreen ? null : clearButton(inputRef)}
        {keyHint && !value ? (
          <span className="pl-kbd" aria-hidden="true">
            /
          </span>
        ) : null}
      </label>
      {focused && !fullScreen ? <div className="pl-search-panel">{panelBody}</div> : null}
      {focused && fullScreen
        ? createPortal(
            <div className="pl-search-screen pl-sheet-root" role="dialog" aria-modal="true" aria-label="Search products">
              <div className="pl-search-screen__bar">
                <label className="pl-search is-on">
                  <PlIcon name="search" />
                  {input(screenInputRef, true)}
                  {clearButton(screenInputRef)}
                </label>
                <button
                  type="button"
                  className="pl-btn is-ghost"
                  onClick={() => {
                    if (value !== openedWith.current) onChange(openedWith.current);
                    setFocused(false);
                  }}
                >
                  Cancel
                </button>
              </div>
              <div className="pl-search-screen__body">{panelBody}</div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default ProductSearchBox;
